import { Link } from "@tanstack/react-router";
import { T, useTranslate } from "@tolgee/react";
import { ArrowRight, TrendingDown, TrendingUp } from "lucide-react";

import { useTradePerpMarkets } from "#/features/trade/hooks/use-trade-data";
import { formatPrice, formatSignedPercent } from "#/lib/formatters";
import { cn } from "#/lib/utils";

import { landingFeaturedMarkets } from "./constants/content.ts";
import { MotionItem, StaggerContainer, scaleIn } from "#/components/ui/motion-primitives";
import { SectionBlock } from "./section-block";

function formatCompactUsdMetric(value: number) {
	return new Intl.NumberFormat("en-US", {
		notation: "compact",
		maximumFractionDigits: value >= 1_000_000_000 ? 2 : 1,
	}).format(value);
}

export function StocksSection() {
	const { t } = useTranslate();
	const { markets } = useTradePerpMarkets();
	const marketsByRouteSymbol = new Map(
		markets.map((market) => [market.routeSymbol, market]),
	);

	return (
		<SectionBlock
			id="featured-markets"
			label={t("stocks.section-label")}
			title={
				<T
					keyName="stocks.section-title"
					params={{
						light: <span />,
						accent: <span className="text-primary" />,
						break: <br />,
					}}
				/>
			}
			description={t("stocks.section-description")}
			centered
		>
			<StaggerContainer
				className="mx-auto mb-18 grid w-[calc(100%-24px)] max-w-275 gap-4 px-0 md:w-[calc(100%-72px)] md:grid-cols-2 xl:grid-cols-3"
				stagger={0.1}
			>
				{landingFeaturedMarkets.map((featuredMarket) => {
					const market = marketsByRouteSymbol.get(featuredMarket.routeSymbol);
					const translatedName = t(featuredMarket.nameKey);
					const name =
						translatedName === featuredMarket.nameKey
							? featuredMarket.fallbackName
							: translatedName;
					const displaySymbol =
						market?.displaySymbol ?? featuredMarket.fallbackDisplaySymbol;
					const symbol = market?.coin ?? featuredMarket.fallbackSymbol;
					const isPositive = (market?.priceChangePercent ?? 0) >= 0;
					const description = t(featuredMarket.descriptionKey);

					return (
						<MotionItem
							key={featuredMarket.routeSymbol}
							variants={scaleIn}
							className="h-full"
						>
							<Link
								to="/trade/$symbol"
								params={{ symbol: featuredMarket.routeSymbol }}
								className="group relative flex h-full flex-col overflow-hidden rounded-[24px] border border-trade-border-subtle/50 p-6 transition hover:-translate-y-1"
								style={
									{
										background: `radial-gradient(circle at top right, color-mix(in oklab, ${featuredMarket.accent} 14%, transparent), transparent 50%), linear-gradient(180deg, color-mix(in oklab, var(--color-primitives-coolneutral-15) 96%, transparent) 0%, color-mix(in oklab, var(--color-primitives-coolneutral-7) 98%, transparent) 100%)`,
										"--card-accent": featuredMarket.accent,
									} as React.CSSProperties
								}
								onMouseEnter={(e) => {
									(e.currentTarget as HTMLElement).style.borderColor =
										`color-mix(in oklab, ${featuredMarket.accent} 40%, transparent)`;
								}}
								onMouseLeave={(e) => {
									(e.currentTarget as HTMLElement).style.borderColor = "";
								}}
							>
								<div
									className="absolute inset-x-6 top-0 h-px opacity-70"
									style={{
										background: `linear-gradient(90deg, transparent, color-mix(in oklab, ${featuredMarket.accent} 40%, transparent), transparent)`,
									}}
								/>
								<div className="flex flex-1 items-start justify-between gap-4">
									<div className="min-w-0 flex-1">
										<div className="flex items-center gap-2">
											<span className="text-title1 text-foreground">
												{name}
											</span>
										</div>
										<div className="mt-1 text-label2-medium text-muted-foreground">
											{displaySymbol}
										</div>
										<p className="mt-3 text-label1 text-muted-foreground">
											{description}
										</p>
									</div>
									<div className="text-right">
										<div className="text-title1 text-foreground">
											{market ? `$${formatPrice(market.markPrice)}` : "--"}
										</div>
										<div
											className={cn(
												"mt-2 flex items-center justify-end gap-1 text-body2-bold",
												isPositive ? "text-trade-green" : "text-trade-red",
											)}
										>
											{isPositive ? (
												<TrendingUp className="h-3.5 w-3.5" />
											) : (
												<TrendingDown className="h-3.5 w-3.5" />
											)}
											{market
												? formatSignedPercent(market.priceChangePercent)
												: "--"}
										</div>
									</div>
								</div>

								<div className="mt-auto grid grid-cols-3 gap-3 pt-5">
									<div className="rounded-2xl border border-[var(--color-theme-static-white)]/6 bg-[var(--color-theme-static-white)]/3 px-3 py-3">
										<div className="text-label2 text-muted-foreground">
											{t("stocks.leverage-label")}
										</div>
										<div className="mt-0.5 text-body2-bold text-primary">
											{market ? `${market.maxLeverage}x` : "--"}
										</div>
									</div>
									<div className="rounded-2xl border border-[var(--color-theme-static-white)]/6 bg-[var(--color-theme-static-white)]/3 px-3 py-3">
										<div className="text-label2 text-muted-foreground">
											{t("stocks.volume-label")}
										</div>
										<div className="mt-0.5 text-body2-bold text-foreground">
											{market
												? `$${formatCompactUsdMetric(market.dayNotionalVolume)}`
												: "--"}
										</div>
									</div>
									<div className="rounded-2xl border border-[var(--color-theme-static-white)]/6 bg-[var(--color-theme-static-white)]/3 px-3 py-3">
										<div className="text-label2 text-muted-foreground">
											{t("stocks.oi-label")}
										</div>
										<div className="mt-0.5 text-body2-bold text-foreground">
											{market
												? `$${formatCompactUsdMetric(market.openInterestValue)}`
												: "--"}
										</div>
									</div>
								</div>

								<div className="mt-5 flex items-center justify-center gap-2 rounded-xl border border-primary/16 bg-primary/8 px-4 py-2.5 text-label1-bold text-primary transition group-hover:bg-primary/14">
									{t("stocks.card-cta", { symbol })}
									<ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
								</div>
							</Link>
						</MotionItem>
					);
				})}
			</StaggerContainer>
		</SectionBlock>
	);
}
